import { Grow } from '@mui/material';
import { timeDay } from 'd3';
import dateFns from 'date-fns';
import _ from 'lodash';
import Markdown from 'markdown-to-jsx';
import React, { useContext } from 'react';
import { MessagingContext } from '../../../services';
import { AttachmentButton } from '../attachment-button';
import { NoteIndicator } from '../note-indicator';
import { authorNickname, IMessagingComment } from '../util';
import * as S from './chat-history.styles';

interface IChatHistory {
    hideNote?: boolean;
}

export const ChatHistory: React.FunctionComponent<IChatHistory> = (props: IChatHistory): React.ReactElement => {
    const { hideNote } = props;
    const context = useContext(MessagingContext);
    const { comments, documents } = context;

    const mdyFormat = (d: Date): string => dateFns.format(d, 'MMM D, YYYY');
    const timeFormat = (d: Date): string => dateFns.format(d, 'h:mm A');
    const parse = (d: string | undefined): Date => (d ? dateFns.parse(d) : new Date());

    const isNewDay = (currDate?: Date, nextDate?: Date): boolean => {
        let isSameDay = false;
        if (currDate && nextDate) {
            isSameDay = timeDay.count(currDate, nextDate) <= 0;
        }
        return !isSameDay;
    };

    // NB: skip first comment, as it is shown in the chat body
    const renderAttachments = (comment: IMessagingComment): React.ReactElement[] | undefined => {
        const commentDocs = _.filter(documents, document => _.includes(comment.documents, document.id));
        return _.times(
            _.size(commentDocs),
            (i: number): React.ReactElement => {
                const doc = commentDocs[i];
                const onDownload = async (): Promise<void> => {
                    if (doc.id) {
                        await (context.onDocumentDownload && context.onDocumentDownload(doc.id));
                    }
                };

                const onAssign = async (): Promise<void> => {
                    await (context.onDocumentAssign && context.onDocumentAssign(doc));
                };

                const canAssign = context.message?.claimId && !doc.claimId && !!context.onDocumentAssign;

                return (
                    <S.HistoryEntryAttachment key={`attachment-${i}`}>
                        <AttachmentButton
                            key={`attachment-${i}`}
                            filename={doc.filename as string}
                            filesize={doc.filesize}
                            thumbnail={doc.thumbnail}
                            onDownload={onDownload}
                            onAssign={canAssign ? onAssign : undefined}
                        />
                    </S.HistoryEntryAttachment>
                );
            },
        );
    };

    const renderHeader = (date: Date, i: number): React.ReactElement => (
        <S.HistoryHeader key={`header-${date}${i}`}>
            <S.HistoryHeaderLine />
            <S.HistoryHeaderLabel>{mdyFormat(date)}</S.HistoryHeaderLabel>
            <S.HistoryHeaderLine />
        </S.HistoryHeader>
    );

    const renderComment = (comment: IMessagingComment, i: number): React.ReactElement => {
        const hasNote = _.get(comment, 'note', false);
        return (
            <Grow in={true} key={comment.id}>
                <S.HistoryEntry note={hasNote}>
                    <S.HistoryEntryTopic>
                        <S.HistoryAuthorIndicator unread={false} author={comment.author} />
                        <S.HistoryEntryName>{authorNickname(comment)}</S.HistoryEntryName>
                        <S.HistoryEntryTime>{timeFormat(parse(comment.created))}</S.HistoryEntryTime>
                        <S.HistoryNoteSpacer />
                        {hasNote && <NoteIndicator comment={comment} />}
                    </S.HistoryEntryTopic>
                    <S.HistoryEntryMessage>
                        <Markdown>{comment.body}</Markdown>
                    </S.HistoryEntryMessage>
                    {renderAttachments(comment)}
                </S.HistoryEntry>
            </Grow>
        );
    };

    const renderComments = (): React.ReactElement[] => {
        const result: React.ReactElement[] = [];
        let lastDay: Date | undefined;
        const historyComments = _.tail<IMessagingComment>(comments as IMessagingComment[]);
        _.forEach(historyComments, (comment: IMessagingComment | undefined, i: number): void => {
            if (comment) {
                if (hideNote && comment.note) {
                    return;
                }
                const date = parse(comment.created);
                if (isNewDay(lastDay, date)) {
                    result.push(renderHeader(date, i));
                }
                result.push(renderComment(comment, i));
                lastDay = date;
            }
        });
        return result;
    };

    return (
        <S.History>
            <S.HistoryBody>{renderComments()}</S.HistoryBody>
        </S.History>
    );
};
